/**
 * Apples to Apples on the TV: read-only, everything off `viewShared`. TV control grants
 * nothing here, since the game has no host requests or client actions.
 */

import { useEffect, useState } from 'react';
import { JoinQr, Scoreboard, Timer, type GameUiProps } from '@gsf/kit';
import { applesView, type ApplesView } from './view';

export function ApplesDisplay({ snapshot, events, connected }: GameUiProps) {
  const view = applesView(snapshot);
  const { core, shared } = view;

  // Flash the stage when the cards land — the beat between "everyone is playing" and
  // "the judge is reading" is the one the room looks up for.
  const cardsInN = events.cardsIn?.n;
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    if (cardsInN === undefined) return;
    setFlash(true);
    const timer = setTimeout(() => setFlash(false), 500);
    return () => clearTimeout(timer);
  }, [cardsInN]);

  return (
    <div className="tv">
      <div className="tv-header">
        <h1 className="tv-title">{core.title}</h1>
        <span className="spacer" />
        {shared.phase === 'lobby' || shared.phase === 'over' ? null : (
          <span className="tv-round">
            {shared.winCondition === 'firstToN'
              ? `Round ${shared.round} · first to ${shared.winTarget}`
              : shared.winCondition === 'unlimited'
                ? `Round ${shared.round}`
                : `Round ${shared.round} of ${shared.rounds}`}
          </span>
        )}
        {!connected ? <span className="tag tag-bad">reconnecting…</span> : null}
      </div>

      <div className="tv-main">
        <div className="tv-stage-col">
          <div className={`tv-stage${flash ? ' buzz-flash' : ''}`}>
            <Stage view={view} />
          </div>
        </div>

        <div className="tv-side">
          <div className="panel">
            <h2 className="panel-title">Green apples won</h2>
            <Scoreboard
              players={view.standings}
              activeParticipantId={shared.judgeId}
              showPresence
            />
          </div>
          {shared.phase === 'playing' ? (
            <div className="panel">
              <h2 className="panel-title">Cards in</h2>
              <ul className="apples-waiting">
                {view.standings
                  .filter((player) => player.id !== shared.judgeId)
                  .map((player) => (
                    <li key={player.id}>
                      <span>{player.name}</span>
                      <span className="spacer" />
                      {shared.playedBy.includes(player.id) ? (
                        <span className="tag tag-good">in</span>
                      ) : (
                        <span className="faint">…</span>
                      )}
                    </li>
                  ))}
              </ul>
            </div>
          ) : null}
        </div>
      </div>

      <div className="tv-footer">
        <JoinQr code={core.code} />
        <span className="spacer" />
        <span className="faint">
          {view.standings.filter((player) => player.connected).length} of{' '}
          {view.standings.length} players connected
        </span>
      </div>
    </div>
  );
}

/** The middle of the screen, which is a different thing in each of the six phases. */
function Stage({ view }: { view: ApplesView }) {
  const { shared, core } = view;
  const judge = view.judgeName ?? 'someone';

  if (shared.phase === 'lobby') {
    return (
      <div className="apples-stage">
        <p className="apples-headline">Apples to Apples</p>
        <p className="faint">
          {shared.readyBy.length} of {view.standings.length} ready
          {view.standings.length < shared.minPlayers
            ? ` — ${String(shared.minPlayers)} players needed`
            : ''}
        </p>
      </div>
    );
  }

  if (shared.phase === 'over') {
    const [winner] = view.standings;
    return (
      <div className="apples-stage">
        <p className="apples-headline">
          {winner ? `${winner.name} wins!` : 'That’s the game'}
        </p>
        <p className="faint">
          {shared.rounds} round{shared.rounds === 1 ? '' : 's'} played
        </p>
      </div>
    );
  }

  if (shared.phase === 'prompt') {
    return (
      <div className="apples-stage">
        <p className="apples-judge">{judge} is picking a green apple…</p>
        <div className="apples-green apples-green-back">?</div>
      </div>
    );
  }

  return (
    <div className="apples-stage">
      <p className="apples-judge">
        {judge} is judging
        {shared.phase === 'playing' ? ` — ${String(shared.awaiting.length)} still to play` : ''}
      </p>
      {shared.green ? <div className="apples-green">{shared.green.text}</div> : null}

      {shared.phase === 'playing' ? (
        <>
          {/* Face down, one per card in. A count is not a secret. */}
          <div className="apples-table">
            {shared.playedBy.map((id) => (
              <div key={id} className="apples-card is-back" />
            ))}
          </div>
          <Timer endsAt={core.deadlineAt} serverNow={core.serverNow} />
        </>
      ) : (
        <div className="apples-table">
          {shared.table.map((play) => (
            <div
              key={play.cardId}
              className={`apples-card${
                shared.winner?.cardId === play.cardId ? ' is-winner' : ''
              }`}
            >
              <span className="apples-card-text">{play.text}</span>
              {/* Null until the reveal: `viewShared` redacts who played what. */}
              {play.participantId ? (
                <span className="apples-card-owner">{view.nameOf(play.participantId)}</span>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {shared.phase === 'reveal' ? (
        <>
          {shared.winner ? null : <p className="faint">Nobody played a card.</p>}
          {/* No countdown: any player leaves the reveal from their phone, so the TV says
              whose move it is. */}
          <p className="apples-judge">
            {shared.round >= shared.rounds
              ? 'Tap "See the scores" on any phone.'
              : 'Tap "Next round" on any phone when you’re ready.'}
          </p>
        </>
      ) : null}
    </div>
  );
}
